import { createElement } from 'react';
import {
  RiAB,
  RiAlarmWarningLine,
  RiArchiveLine,
  RiArrowLeftRightLine,
  RiBarChart2Line,
  RiBaseStationLine,
  RiBox3Line,
  RiBuilding2Line,
  RiCalendarLine,
  RiCompass3Line,
  RiContrastDropLine,
  RiDashboard2Line,
  RiDashboard3Line,
  RiDashboardHorizontalLine,
  RiDashboardLine,
  RiDatabase2Line,
  RiDropFill,
  RiDropLine,
  RiEarthquakeLine,
  RiFileChartLine,
  RiFileGifLine,
  RiFlashlightLine,
  RiFloodLine,
  RiFocus3Line,
  RiHeading,
  RiHome2Line,
  RiImageLine,
  RiInformationLine,
  RiLineChartLine,
  RiMapPin2Line,
  RiMapPinLine,
  RiPlayLine,
  RiPulseLine,
  RiRfidFill,
  RiRfidLine,
  RiRulerLine,
  RiSettings3Line,
  RiShapesLine,
  RiSpeedUpLine,
  RiStackLine,
  RiTableLine,
  RiTempColdLine,
  RiTempHotLine,
  RiText,
  RiThermometerLine,
  RiTimeLine,
  RiTimerLine,
  RiWaterFlashLine,
  RiWaterPercentLine,
  RiWindyLine,
  type RemixiconComponentType,
} from '@remixicon/react';
import { Icon } from '@/shared/ui/icon';

/**
 * Icons whose names are stored in documents (section icon, indicator icon), e.g. `"drop-line"`.
 * The constructor's own static set (sensor families used by the seed and the pickers) renders synchronously —
 * dashboards show these on first paint; any other name goes to the UI kit's `<Icon name>` (lazy registry).
 */
const ICONS: Record<string, RemixiconComponentType> = {
  'a-b': RiAB,
  'alarm-warning-line': RiAlarmWarningLine,
  'archive-line': RiArchiveLine,
  'arrow-left-right-line': RiArrowLeftRightLine,
  'bar-chart-2-line': RiBarChart2Line,
  'base-station-line': RiBaseStationLine,
  'box-3-line': RiBox3Line,
  'building-2-line': RiBuilding2Line,
  'calendar-line': RiCalendarLine,
  'compass-3-line': RiCompass3Line,
  'contrast-drop-line': RiContrastDropLine,
  'dashboard-2-line': RiDashboard2Line,
  'dashboard-3-line': RiDashboard3Line,
  'dashboard-horizontal-line': RiDashboardHorizontalLine,
  'dashboard-line': RiDashboardLine,
  'database-2-line': RiDatabase2Line,
  'drop-fill': RiDropFill,
  'drop-line': RiDropLine,
  'earthquake-line': RiEarthquakeLine,
  'file-chart-line': RiFileChartLine,
  'file-gif-line': RiFileGifLine,
  'flashlight-line': RiFlashlightLine,
  'flood-line': RiFloodLine,
  'focus-3-line': RiFocus3Line,
  heading: RiHeading,
  'home-2-line': RiHome2Line,
  'image-line': RiImageLine,
  'information-line': RiInformationLine,
  'line-chart-line': RiLineChartLine,
  'map-pin-2-line': RiMapPin2Line,
  'map-pin-line': RiMapPinLine,
  'play-line': RiPlayLine,
  'pulse-line': RiPulseLine,
  'rfid-fill': RiRfidFill,
  'rfid-line': RiRfidLine,
  'ruler-line': RiRulerLine,
  'settings-3-line': RiSettings3Line,
  'speed-up-line': RiSpeedUpLine,
  'stack-line': RiStackLine,
  'table-line': RiTableLine,
  'temp-cold-line': RiTempColdLine,
  'temp-hot-line': RiTempHotLine,
  text: RiText,
  'thermometer-line': RiThermometerLine,
  'time-line': RiTimeLine,
  'timer-line': RiTimerLine,
  'water-flash-line': RiWaterFlashLine,
  'water-percent-line': RiWaterPercentLine,
  'windy-line': RiWindyLine,
};

/** Names available in pickers (section / indicator icon grids). */
export const DATA_ICON_NAMES = Object.keys(ICONS);

/** The constructor's own component for a stored name, or null (→ UI kit registry). */
export const resolveDataIcon = (name: string | null | undefined): RemixiconComponentType | null => (name && Object.hasOwn(ICONS, name) ? ICONS[name]! : null);

export interface DataIconProps {
  name: string | null | undefined;
  className?: string;
  /** design px are handled by `className` (`size-24`); `size` is only for non-rem contexts */
  size?: number | string;
  'aria-hidden'?: boolean;
}

export function DataIcon({ name, className, size, ...rest }: DataIconProps) {
  const own = resolveDataIcon(name);
  if (own) return createElement(own, { className, size: size ?? '1em', 'aria-hidden': rest['aria-hidden'] ?? true });
  if (!name) return createElement(RiShapesLine, { className, size: size ?? '1em', 'aria-hidden': true });
  return <Icon name={name} className={className} />;
}
